import { cn } from '@/lib/utils';
import { deploymentModels, deploymentNodes, deploymentOptions } from '../data';
import type { ModelId, Point } from '../types';

const NODE_WIDTH = 180;
const NODE_HEIGHT = 64;

// Connect the nearest horizontal faces, or the vertical faces of aligned nodes.
function getConnection(from: Point, to: Point) {
  const [fromX, fromY] = from;
  const [toX, toY] = to;
  const vertical = Math.abs(toX - fromX) < 40;
  const start = vertical
    ? [fromX + NODE_WIDTH / 2, toY > fromY ? fromY + NODE_HEIGHT : fromY]
    : [toX > fromX ? fromX + NODE_WIDTH : fromX, fromY + NODE_HEIGHT / 2];
  const end = vertical
    ? [toX + NODE_WIDTH / 2, toY > fromY ? toY : toY + NODE_HEIGHT]
    : [toX > fromX ? toX : toX + NODE_WIDTH, toY + NODE_HEIGHT / 2];
  const x = (start[0] + end[0]) / 2;
  const y = (start[1] + end[1]) / 2;

  return {
    x,
    y,
    path: vertical
      ? `M${start} L${end}`
      : `M${start} C${x},${start[1]} ${x},${end[1]} ${end}`,
  };
}

interface ArchitectureDiagramProps {
  model: ModelId;
  showMigration: boolean;
  activeNode: string | null;
  onSelect: (id: string) => void;
}

const ArchitectureDiagram = ({ model, showMigration, activeNode, onSelect }: ArchitectureDiagramProps) => {
  const current = deploymentModels[model];
  const option = deploymentOptions.find(option => option.id === model)!;
  const edges = current.edges.filter(edge => edge.kind !== 'mig' || showMigration);

  return (
    <div className="overflow-x-auto" tabIndex={0} role="region" aria-label="Deployment architecture; scroll horizontally on smaller screens">
      <svg className="block h-auto w-full min-w-[700px] font-sans" viewBox="0 0 1000 560" role="group" aria-labelledby="deployment-diagram-title">
        <title id="deployment-diagram-title">Deployment architecture diagram</title>
        <defs>
          <marker id="deployment-arrow" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
            <path d="M0,0 L10,5 L0,10 z" fill="#A9B5D6" />
          </marker>
        </defs>
        {current.zones.map(zone => {
          const hospital = zone.client && !zone.dc;
          return (
            <g key={zone.label}>
              <rect
                x={zone.x} y={zone.y} width={zone.w} height={zone.h} rx={16}
                fill={hospital ? '#F7F9FC' : option.tint}
                stroke={hospital ? '#B7C2DC' : option.accent}
                strokeDasharray={hospital ? '5 5' : undefined}
                strokeWidth={1.4}
              />
              <text x={zone.x + 16} y={zone.y + 26} className="text-sm font-semibold" fill={hospital ? '#5A6484' : option.accent}>
                {zone.label}
              </text>
            </g>
          );
        })}
        {edges.map(({ source, target, label, kind }) => {
          const { path, x, y } = getConnection(current.nodes[source], current.nodes[target]);
          const migration = kind === 'mig';
          return (
            <g key={`${model}-${source}-${target}`}>
              <path
                d={path}
                fill="none"
                stroke={migration ? option.accent : '#A9B5D6'}
                strokeWidth={1.6}
                strokeDasharray={migration ? '6 5' : kind === 'opt' ? '3 4' : undefined}
                markerEnd="url(#deployment-arrow)"
              />
              {label && (
                <text x={x} y={y - 6} className="fill-muted-foreground text-[11.5px]" textAnchor="middle" paintOrder="stroke" stroke="#fff" strokeWidth={4}>
                  {label}
                </text>
              )}
              {(migration ? [0] : [0, 0.5]).map(offset => (
                <circle key={offset} r={migration ? 4 : 3.5} fill={option.accent} className="motion-reduce:hidden" aria-hidden="true">
                  <animateMotion path={path} dur={migration ? '8.33s' : '4.55s'} begin={`${-offset * 4.55}s`} repeatCount="indefinite" />
                </circle>
              ))}
            </g>
          );
        })}
        {Object.entries(current.nodes).map(([id, [x, y]]) => {
          const node = deploymentNodes[id];
          const provided = Boolean(node.specs || node.tiers);
          const active = activeNode === id;

          return (
            <g
              key={id}
              className="group cursor-pointer outline-none"
              tabIndex={0}
              role="button"
              aria-label={`${node.title}, ${node.subtitle}`}
              aria-pressed={active}
              aria-controls="deployment-details"
              onClick={() => onSelect(id)}
              onKeyDown={event => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  onSelect(id);
                }
              }}
            >
              <rect
                x={x} y={y} width={NODE_WIDTH} height={NODE_HEIGHT} rx={12}
                fill={active ? option.tint : '#fff'}
                stroke={active ? option.accent : provided ? '#5A6484' : '#B7C2DC'}
                strokeWidth={active ? 2.5 : 1.5}
                className={cn(
                  'transition-[stroke,fill] duration-150 motion-reduce:transition-none',
                  'group-hover:stroke-(--deployment-accent) group-focus-visible:stroke-(--deployment-accent) group-focus-visible:stroke-[3px]',
                )}
              />
              <text x={x + 14} y={y + 27} className="fill-foreground text-[14.5px] font-semibold">{node.title}</text>
              <text x={x + 14} y={y + 46} className="fill-muted-foreground text-xs">{node.subtitle}</text>
              {provided && <circle cx={x + NODE_WIDTH - 10} cy={y + 10} r={4.5} fill={option.accent} />}
            </g>
          );
        })}
      </svg>
    </div>
  );
};

export default ArchitectureDiagram;
